/* eslint-disable camelcase */
import addresser from 'addresser';

// All Primative types
const DEAULT_ADDRESS_OBJ = {
  address: null,
  city: null,
  stateProvidence: null,
  postalCode: null,
  country: null,
  streetNumber: null,
  streetName: null,
};

export default class GoogleAddressParser {
  constructor() {
    this.address = {
      ...DEAULT_ADDRESS_OBJ
    };
  }

  isValid() {
    let valid = true;
    const addressKeys = Object.keys(this.address);
    addressKeys.forEach((k) => {
      if (!this.address[k]) {
        valid = false;
      }
    });
    return valid;
  }

  /**
   * Function to get the formatted addresss from a geocode Google API
   * response (from /place-details on DQ-API) and pull out the relevant pieces of
   * info. Using address components seems to be inconsistent regarding types, as they are
   * not guaranteed to remain the same between network calls with the same address
   * https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingResults
   * Seems to be more consistent to parse based off the fomattedAddress field in the return payload
   * Currently can parse Canada and US addresses
   * @param {string} formattedAddressIn - Human readable address from google Geocode API payload
   * ex: 276 Grand Concourse, Bronx, NY 10451, USA
   * @param {Object} addressComponents - Address component object returned as the result of a Google
   * geocoding API call
   * @param {string} addressInputText - The address text the user typed in to
   * the Google autocomplete; used as a fallback to get street number if
   * the library fails to parse it
   * https://www.npmjs.com/package/addresser
   * @returns {void}
   */
  parseAddress(formattedAddressIn, addressComponents, addressInputText) {
    try {
      if (formattedAddressIn) {
        const {
          zipCode, stateName, placeName, addressLine1, streetNumber,
          streetSuffix, streetName
        } = addresser.parseAddress(formattedAddressIn);
        const splitAddressArr = formattedAddressIn.split(' ');
        this.address.address = addressLine1;
        this.address.city = placeName;
        this.address.stateProvidence = stateName;
        this.address.postalCode = zipCode;
        this.address.country = splitAddressArr[splitAddressArr.length - 1].trim();
        this.address.streetName = `${streetName}`;
        if (streetSuffix) this.address.streetName += ` ${streetSuffix}`;
        this.address.streetNumber = streetNumber
          || GoogleAddressParser.parseStreetNumberFromInputString(addressInputText);
      } else {
        this.address = { ...DEAULT_ADDRESS_OBJ };
      }
    } catch (error) {
      // Passed in address' format throws an error
      this.parseAddressFromAddressComponents(
        addressComponents,
        formattedAddressIn,
        addressInputText
      );
    }
  }

  /**
   * Second function that attempts to parse addresses if the addresser library parse
   * fails, this is mostly for Canadian / other addresses which have a format that causes
   * addresser and throw an error
   * @param {Object} addressComponents - Array of address components from Google
   * geocode response payload
   ** @param {string} addressInputText - The address text the user typed in to
   * the Google autocomplete; used as a fallback to get street number if
   * the library fails to parse it
   */
  parseAddressFromAddressComponents(addressComponents, addressInputText) {
    if (addressComponents) {
      const parsedAddress = addressComponents.reduce((accObj, { long_name, types }) => {
        types.forEach((type) => {
        // If this type was already previously set and there are
        // more types associated with this address portion skip over
        // this type
          if (accObj[type] && types.length > 1) {
            return;
          }
          accObj[type] = long_name;
        });
        return accObj;
      }, {});
      const {
        country, street_number, route, postal_code_prefix, postal_code, administrative_area_level_1,
        administrative_area_level_2, administrative_area_level_3, sublocality_level_1, locality,
        street_name, political
      } = parsedAddress;
      this.address.country = country;
      this.address.streetNumber = street_number
      || GoogleAddressParser.parseStreetNumberFromInputString(addressInputText);
      this.address.streetName = route || street_name || political;
      this.address.postalCode = `${postal_code_prefix === postal_code
      || !postal_code_prefix
        ? postal_code : postal_code_prefix + postal_code}`;
      this.address.stateProvidence = administrative_area_level_1;
      this.address.address = addressInputText;
      // Fallbacks set up in order of address type most likely to contain city
      this.address.city = locality || sublocality_level_1
      || administrative_area_level_2
      || administrative_area_level_3
      || administrative_area_level_1;
    }
  }

  /**
 * Google geocode results seem to often omit the street number during parsing,
 * by manually parsing the street number from the input string we have a backup in case
 * the first 2 street number methods fail parsing
 * @param {string} inputAddressString
 * @returns {string} streetNumber
 */
  static parseStreetNumberFromInputString(inputAddressString) {
    if (!inputAddressString) return null;
    const alphabetRegex = /[A-Za-z]/;
    // Create substring from start to first instance of non-numeric char
    const firstNonNumerialCharPos = inputAddressString.search(alphabetRegex);
    const streetNumber = inputAddressString.substring(0, firstNonNumerialCharPos).trim();
    return streetNumber;
  }

  toJson() {
    const {
      address,
      city,
      stateProvidence,
      postalCode,
      country,
      streetName,
      streetNumber,
    } = this.address;
    return {
      address,
      city,
      stateProvidence,
      postalCode,
      country,
      streetName,
      streetNumber,
    };
  }
}
